import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { FloatingPopup } from '@nx-frontend/shared/ui/floating-popup';
import { generalEnv } from '@nx-frontend/shared/env';
import { hideAiHelperEvent } from '@nx-frontend/shared/store/ai-helper';
import { PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import {
  $aiHelperAgent,
  abortAgentMessageEvent,
  AgentModeMainTabState,
  loadAIHelperAgentEvent,
  resetAIHelperAgentEvent,
  setChatbotAgentModeMainTabStateEvent,
} from './agent-store';
import { ChatbotAgentHistory } from './chatbot-agent-history';
import { ChatbotAgentModeMainTab } from './chatbot-agent-mode-main-tab';
import { ChatbotContext } from './chatbot-context';
import { ChatbotHeader } from './chatbot-header';
import { $activeThreadId, $writingMessage, setActiveThreadIdEvent, updateAiAgentThreadMessagesEvent } from './store';

interface ChatbotAgentModeProps {
  /** @default true */
  backButton?: boolean;

  /** @default true */
  closeButton?: boolean;

  /** @default true */
  chatbotHeader?: boolean;

  fullScreenButton?: boolean;
  noInputLineMode?: boolean;
  headerHistoryButton?: boolean;
  headerNewChatButton?: boolean;
  isPortal?: boolean;

  /** if set and forceStartView is not, then agents without inputs will be run automatically on opening */
  autoRunIfPossible?: boolean;

  /** Whether to scroll smoothly to the initial agent message and to the newly created user messages */
  smoothScroll?: boolean;

  hideHistoryItemsContextMenu?: boolean;

  headerDefaultCursor?: boolean;
  constrainToViewport?: boolean;
}

enum AgentModeTabs {
  CHAT = 0,
  HISTORY = 1,
}

type MainTabState = {
  tab: AgentModeTabs.CHAT;
  threadId?: string;
};

type HistoryTabState = {
  tab: AgentModeTabs.HISTORY;
};

type BaseTabState = MainTabState | HistoryTabState;

type AgentModeTabsHistoryState = BaseTabState & {
  previousState: AgentModeTabsHistoryState | null;
  depth: number;
};

export const ChatbotAgentMode: FC<ChatbotAgentModeProps> = (props: ChatbotAgentModeProps) => {
  const {
    backButton = true,
    closeButton = true,
    chatbotHeader = true,
    isPortal,
    fullScreenButton,
    smoothScroll = false,
    autoRunIfPossible = false,
    constrainToViewport = false,
  } = props;

  const [activeThreadId, aiHelperAgent, writingMessage] = useUnit([$activeThreadId, $aiHelperAgent, $writingMessage]);
  const [
    hideAiHelper,
    setActiveThreadId,
    resetAIHelperAgent,
    loadAIHelperAgent,
    setChatbotAgentModeMainTabState,
    abortAgentMessage,
    updateAiAgentThreadMessages,
  ] = useUnit([
    hideAiHelperEvent,
    setActiveThreadIdEvent,
    resetAIHelperAgentEvent,
    loadAIHelperAgentEvent,
    setChatbotAgentModeMainTabStateEvent,
    abortAgentMessageEvent,
    updateAiAgentThreadMessagesEvent,
  ]);
  const aiContext = useContext(ChatbotContext);

  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const aiAgentStartedLoading = useRef<boolean>();

  const getTabInitialState = useCallback(() => {
    return {
      tab: AgentModeTabs.CHAT,
      threadId: (aiContext.openExistingAgentThread && activeThreadId) || undefined,
      previousState: null,
      depth: 1,
    };
  }, [aiContext.openExistingAgentThread, activeThreadId]);
  const [tabState, setTabState] = useState<AgentModeTabsHistoryState>(getTabInitialState());

  const pushTabState = useCallback((newState: BaseTabState) => {
    setTabState((currentTabState) => ({
      ...newState,
      depth: currentTabState.depth + 1,
      previousState: currentTabState,
    }));
  }, []);

  const popTabState = useCallback(() => {
    setTabState((currentTabState) => currentTabState.previousState || currentTabState);
  }, []);

  const { agent } = aiHelperAgent;

  const loadThreadMessages = useCallback(
    (params: { threadId: string }) => {
      updateAiAgentThreadMessages({
        apiHost: aiContext.apiHost,
        threadId: params.threadId,
        orgId: aiContext.orgId ?? '',
      });
    },
    [aiContext.apiHost, aiContext.orgId, updateAiAgentThreadMessages]
  );

  // Cleaning previous agent
  useEffect(() => {
    if (aiContext.agentId && !aiContext.openExistingAgentThread) {
      resetAIHelperAgent();
      aiAgentStartedLoading.current = false;
      setActiveThreadId(null);
    }
    setTabState(getTabInitialState());
    // Needed only on initial opening and when agent changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiContext.agentId]);

  // Set default state for main tab
  useEffect(() => {
    setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
    return () => {
      setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
    };
  }, [setChatbotAgentModeMainTabState]);

  // Load agent
  useEffect(() => {
    if (
      !aiAgentStartedLoading.current &&
      (aiHelperAgent.state === 'not_loaded' ||
        (aiHelperAgent?.agent?.globalId !== aiContext.agentId && aiContext.agentId))
    ) {
      aiAgentStartedLoading.current = true;
      console.assert(aiContext.orgId, 'orgId is required to be in the aiContext');

      loadAIHelperAgent({
        globalId: aiContext.agentId as string,
        orgId: aiContext.orgId as string,
      });
    }
  }, [aiContext.agentId, aiContext.orgId, aiHelperAgent?.agent?.globalId, aiHelperAgent.state, loadAIHelperAgent]);

  const agentLoaded = useMemo(() => {
    return aiHelperAgent.state === 'loaded' && aiHelperAgent?.agent?.globalId === aiContext.agentId;
  }, [aiContext.agentId, aiHelperAgent.agent?.globalId, aiHelperAgent.state]);

  const showBackButton = useMemo(() => {
    if (!backButton) {
      return false;
    }
    if (!tabState.previousState && agent?.webpageDetails && agent?.inputs.length === 0 && autoRunIfPossible) {
      return false;
    }
    return !!tabState.previousState || (tabState.tab === AgentModeTabs.CHAT && !!tabState.threadId);
  }, [agent?.inputs.length, agent?.webpageDetails, autoRunIfPossible, backButton, tabState]);

  const showNewChatButton: boolean = useMemo(() => {
    if (!props.headerNewChatButton) {
      return false;
    }
    return !!tabState.previousState || (tabState.tab === AgentModeTabs.CHAT && !!tabState.threadId);
  }, [props.headerNewChatButton, tabState]);

  const abortMessage = useCallback(() => {
    if (!aiContext.orgId) {
      return console.error('No orgId provided, fail to cancel agent message');
    }
    abortAgentMessage({
      sessionId: aiContext.sessionId,
      orgId: aiContext.orgId,
      messageId: writingMessage?.id ?? '',
      text: writingMessage?.message ?? '',
      dontSendDeleteMessageReq: !writingMessage?.id,
    });
  }, [writingMessage, aiContext.orgId, aiContext.sessionId, abortAgentMessage]);

  // stop writing message if unmounted while writing + reset thread id.
  // needed for correct behaviour on switching pages in portal
  useEffect(() => {
    return () => {
      abortMessage();
      setActiveThreadId(null);
    };
    // This effect should be run only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClose = () => {
    hideAiHelper({
      apiHost: aiContext.apiHost,
      sessionId: aiContext.sessionId as string,
      workspaceId: aiContext.workspaceId,
    });
    setActiveThreadId(null);
    abortMessage();
  };

  const onFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Needed to hightlight a correct thread in history after clicking "Back"
  const [lastActiveThreadId, setLastActiveThreadId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (activeThreadId) {
      setLastActiveThreadId(activeThreadId);
    }
    if (tabState.depth === 1 && tabState.tab === AgentModeTabs.CHAT && !tabState.threadId) {
      setLastActiveThreadId(undefined);
    }
  }, [activeThreadId, tabState]);

  // Effect for setting activeThreadId on changing tab state
  useEffect(() => {
    if (tabState.tab === AgentModeTabs.CHAT) {
      if (tabState.threadId) {
        setActiveThreadId(tabState.threadId);
      } else {
        setActiveThreadId(null);
      }
    } else {
      setActiveThreadId(null);
    }
  }, [setActiveThreadId, tabState.tab, tabState]);

  // If activeThreadId changed while we are in the chat state, we have to update current state
  useEffect(() => {
    if (
      tabState.depth === 1 &&
      tabState.tab === AgentModeTabs.CHAT &&
      !tabState.threadId &&
      activeThreadId &&
      activeThreadId !== tabState.threadId
    ) {
      setTabState((currentTabState) => ({
        ...currentTabState,
        threadId: activeThreadId,
      }));
    }
  }, [activeThreadId, tabState.tab, tabState]);

  const goToHistory = useCallback(() => {
    if (tabState.tab === AgentModeTabs.CHAT) {
      if (tabState.depth === 1) {
        pushTabState({
          tab: AgentModeTabs.HISTORY,
        });
      } else if (tabState.depth === 3) {
        popTabState();
      }
    }
  }, [popTabState, pushTabState, tabState.depth, tabState.tab]);

  const onHistoryItemClick = useCallback(
    (threadId: string) => {
      if (threadId) {
        pushTabState({
          tab: AgentModeTabs.CHAT,
          threadId: threadId,
        });
        loadThreadMessages({ threadId });
      }
    },
    [loadThreadMessages, pushTabState]
  );

  const onHistoryClick = () => {
    goToHistory();
  };

  const onBack = () => {
    if (!tabState.previousState) {
      window.parent.postMessage(
        {
          source: 'ai-helper',
          type: 'backToAgentsList',
        },
        '*'
      );
      setActiveThreadId(null);
      setTabState((state) => ({
        ...state,
        threadId: undefined,
      }));
    } else {
      popTabState();
    }
  };

  const onNewChatClick = useCallback(() => {
    setTabState({
      tab: AgentModeTabs.CHAT,
      depth: 1,
      previousState: null,
    });
    setActiveThreadId(null);
  }, [setActiveThreadId]);

  const body = (
    <div className={classNames('flex flex-col flex-grow max-h-full h-full relative gap-4')}>
      {chatbotHeader && (
        <ChatbotHeader
          debugThreadId={generalEnv.env !== 'prod' && activeThreadId ? activeThreadId : undefined}
          showInfoMenu={false}
          title={agent?.title ?? ''}
          onBack={showBackButton ? onBack : undefined}
          onClose={closeButton ? onClose : undefined}
          // onFullScreen={}
          isFullscreen={isFullscreen}
          fullScreenButton={fullScreenButton}
          themeColors={aiContext.themeColors as ThemeColorSchemas}
          themeStyle={aiContext.themeStyle as PortalStyleConfig}
          onUpdateWorkspaceIndexing={(value) => {
            /* not used */
          }}
          onFullscreenToggle={onFullscreenToggle}
          isPortal={isPortal}
          historyButton={props.headerHistoryButton}
          onHistoryClick={onHistoryClick}
          newChatButton={showNewChatButton}
          onNewChatClick={onNewChatClick}
          cursorMove={!props.headerDefaultCursor}
        />
      )}
      <div className="grow min-h-0">
        {tabState.tab === AgentModeTabs.CHAT && (
          <ChatbotAgentModeMainTab
            agentLoaded={agentLoaded}
            agent={agent}
            startWithExistingThread={!!tabState.threadId}
            noInputLineMode={props.noInputLineMode}
            autoRunIfPossible={autoRunIfPossible}
            smoothScroll={smoothScroll}
            constrainToViewport={constrainToViewport}
          />
        )}
        {tabState.tab === AgentModeTabs.HISTORY && (
          <div className="h-full">
            <ChatbotAgentHistory
              onThreadClick={onHistoryItemClick}
              hideItemsContextMenu={props.hideHistoryItemsContextMenu}
              constrainToViewport={constrainToViewport}
              highlightedThreadId={lastActiveThreadId}
            />
          </div>
        )}
      </div>
    </div>
  );

  if (isFullscreen) {
    return (
      <FloatingPopup
        show={isFullscreen}
        position="screen_full"
        containerClassName={['flex', 'flex-col']}
        onClose={() => setIsFullscreen(false)}
        closeButton={false}
        closeByOutsideClick={false}
        rootContainerClassName={['!z-40']}
      >
        {body}
      </FloatingPopup>
    );
  }
  return body;
};
